import { z } from 'zod';

export const emailSchema = z.string().email({ message: 'Enter a valid email address.' }).max(254);

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be 128 characters or less');

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema.optional(),
  name: z.string().min(1).max(120).optional(),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms to continue.' }),
  }),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

export const magicLinkRequestSchema = z.object({
  email: emailSchema,
  redirectTo: z.string().optional(),
});

export const magicLinkVerifySchema = z.object({
  token: z.string().min(16).max(256),
});

export const oauthProviderSchema = z.enum(['google', 'github', 'discord']);

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;
export type OAuthProvider = z.infer<typeof oauthProviderSchema>;

export const sessionUserSchema = z.object({
  id: z.string().uuid(),
  email: emailSchema,
  name: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  createdAt: z.string().datetime(),
});

export const organizationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  planCode: z.enum(['free', 'starter', 'pro', 'enterprise']),
  trialEndsAt: z.string().datetime().nullable(),
  suspendedAt: z.string().datetime().nullable(),
});

export const membershipRoleSchema = z.enum(['owner', 'admin', 'editor', 'viewer']);

export const membershipSchema = z.object({
  organizationId: z.string().uuid(),
  role: membershipRoleSchema,
  organization: organizationSchema,
});

export const meResponseSchema = z.object({
  user: sessionUserSchema,
  memberships: z.array(membershipSchema),
  activeOrganizationId: z.string().uuid().nullable(),
  featureFlags: z.record(z.boolean()),
});

export type SessionUser = z.infer<typeof sessionUserSchema>;
export type Organization = z.infer<typeof organizationSchema>;
export type Membership = z.infer<typeof membershipSchema>;
export type MembershipRole = z.infer<typeof membershipRoleSchema>;
export type MeResponse = z.infer<typeof meResponseSchema>;
